// event-links.js — lets a number on any page open the Events page showing
// exactly the events it counts: the same event types, time window, chat,
// file and person. The Events page fetches that set from the server
// (pages/events.js), so the list and the number agree even when the events
// are older than the latest-200 feed.
//
// A link is any element with data-ev-link plus:
//   data-ev-types   comma list of event types
//   data-ev-since   a window like 24h / 30d, or an ISO time
//   data-ev-until   an ISO time (optional)
//   data-ev-agent   one chat id (optional)
//   data-ev-resource one resource as stored (optional)
//   data-ev-actor   one person, for "My chats" (optional)
//   data-ev-label   what the list shows, e.g. "Collisions prevented · last 30 days"
//   data-ev-source  "local" for this computer's own events (the Protection
//                   page counts those); otherwise the team's
// A type may carry its own start time, "write.unmanaged_conflict@<ISO>", for
// a number that counts some types over a shorter span (local source only).
import { state } from "./state.js";
import { navigate } from "./router.js";
import { escapeHTML } from "./util.js";
import { renderEvents } from "./pages/events.js";

const FIELDS = ["types", "since", "until", "agent", "resource", "actor", "label", "source"];

function attrName(f) {
  return "data-ev-" + f;
}

// eventsLinkAttrs returns the attributes for an HTML template.
export function eventsLinkAttrs(spec) {
  let out = ' data-ev-link="1" role="link" tabindex="0"';
  for (const f of FIELDS) {
    const v = f === "types" ? (spec.types || []).join(",") : spec[f];
    if (v) out += " " + attrName(f) + '="' + escapeHTML(String(v)) + '"';
  }
  return out;
}

// linkToEvents turns an element already on the page into a link.
export function linkToEvents(el, spec) {
  if (!el) return;
  el.setAttribute("data-ev-link", "1");
  el.setAttribute("role", "link");
  el.setAttribute("tabindex", "0");
  for (const f of FIELDS) {
    const v = f === "types" ? (spec.types || []).join(",") : spec[f];
    if (v) el.setAttribute(attrName(f), String(v));
    else el.removeAttribute(attrName(f));
  }
}

export function specFromElement(el) {
  const spec = {};
  for (const f of FIELDS) {
    const v = el.getAttribute(attrName(f));
    if (v) spec[f] = f === "types" ? v.split(",").filter(Boolean) : v;
  }
  return spec;
}

export function openEvents(spec) {
  state.eventQuery = spec;
  state.eventQueryPage = 0;
  navigate("events");
  renderEvents();
}

// windowLabel turns a window into words: "24h" → "last 24 hours".
export function windowLabel(since) {
  const m = /^(\d+)([hd])$/.exec(since || "");
  if (!m) return "";
  let n = Number(m[1]);
  let unit = m[2];
  if (unit === "h" && n > 24 && n % 24 === 0) {
    n /= 24;
    unit = "d";
  }
  if (unit === "h") return n === 24 ? "last 24 hours" : n === 1 ? "last hour" : "last " + n + " hours";
  return n === 1 ? "last day" : "last " + n + " days";
}

function bind() {
  const go = (e) => {
    const el = e.target.closest && e.target.closest("[data-ev-link]");
    if (!el) return false;
    e.preventDefault();
    e.stopPropagation();
    openEvents(specFromElement(el));
    return true;
  };
  document.addEventListener("click", go, true);
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Enter" || e.key === " ") go(e);
    },
    true,
  );
}

if (typeof document !== "undefined") bind();
